import useSWRInfinite from "swr/infinite";
import { endilApiClient } from "~/lib/client/endilApiClient";
import type {
  FetchPostRequest,
  LoadPostsResponse,
} from "~/lib/server/fetchPosts";
import { Post } from "~/types/asgard/Post";

export type UsePaginatedPostsConfig = {
  authorFilter?: string;
  categoryIdFilter?: number;
  excludePostIdsFilter?: number[];
  pageSize: number;
  tagFilter?: string;
};

export type UsePaginatedPostsResult = {
  isLoadingPosts: boolean;
  posts: Post[];
  isLoadingMore: boolean;
  isEmpty: boolean;
  isReachingEnd: boolean;
  currentPage: number;
  loadNextPage: () => void;
};

export const usePaginatedPosts = ({
  authorFilter,
  categoryIdFilter,
  excludePostIdsFilter,
  pageSize,
  tagFilter,
}: UsePaginatedPostsConfig): UsePaginatedPostsResult => {
  const { data, isLoading, isValidating, error, setSize, size } =
    useSWRInfinite<Post[]>(
      (currentSWRPage: number) => ({
        authorFilterParam: authorFilter,
        categoryIdParam: categoryIdFilter,
        currentPageParam: currentSWRPage + 1,
        excludeIdsParam: excludePostIdsFilter,
        pageSizeParam: pageSize,
        tagFilterParam: tagFilter,
      }),
      async ({
        authorFilterParam,
        categoryIdParam,
        currentPageParam,
        excludeIdsParam,
        pageSizeParam,
        tagFilterParam,
      }) => {
        const response = await endilApiClient.get<LoadPostsResponse>(
          "/api/posts",
          {
            params: {
              authorFilter: authorFilterParam,
              categoryIdFilter: categoryIdParam,
              currentPage: currentPageParam,
              excludePostIdsFilter: excludeIdsParam?.join(","),
              pageSize: pageSizeParam,
              tagFilter: tagFilterParam,
            } satisfies FetchPostRequest,
          }
        );

        return response.data.posts;
      },
      {
        revalidateOnFocus: false,
      }
    );

  if (error) {
    throw error;
  }

  const posts = data?.flat() ?? [];

  const isEmpty = data?.[0]?.length === 0;

  const isReachingEnd =
    (isEmpty || (data && data[data.length - 1]?.length < pageSize)) ?? false;

  const loadNextPage = () => {
    setSize(size + 1);
  };

  return {
    isLoadingPosts: isLoading,
    posts: posts.flat(),
    isLoadingMore: isValidating,
    isEmpty,
    isReachingEnd,
    currentPage: size,
    loadNextPage,
  };
};
